import { CreditCard, CheckCircle, Clock, AlertCircle, XCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card";
import { Badge } from "@/modules/shared/ui/components/badge";
import { Button } from "@/modules/shared/ui/components/button";
import { Alert, AlertDescription, AlertTitle } from "@/modules/shared/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog";
import type { SubscriptionStatusDto } from "../../dtos/subscription-status.dto";
import { cancelSubscription } from "../../infrastructure/subscription.api";
import { toast } from "sonner"
import { TooltipWrap } from "@/modules/shared/ui/components/tooltip-wrap";

interface SubscriptionCardProps {
  subscription: SubscriptionStatusDto;
  onCancel?: () => void;
  onChangePlan?: () => void;
}

const STATUS_DESCRIPTIONS: Record<string, string> = {
  Active: "Your subscription is active and in good standing.",
  Trialing: "You're on a free trial. Full access continues until the trial ends.",
  PastDue:
    "Your last payment failed. You'll keep access until the grace period ends, then the whole organization will be locked out until payment is resolved.",
  Cancelled:
    "Your subscription is set to cancel. You'll keep access until the current billing period ends.",
  Ended: "This subscription has ended. Reactivate a plan to regain access.",
};

export function SubscriptionCard({ subscription, onCancel }: SubscriptionCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = () => {
    const statusConfig: Record<
      string,
      { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }
    > = {
      Active: { variant: "default", icon: CheckCircle },
      Trialing: { variant: "secondary", icon: Clock },
      PastDue: { variant: "destructive", icon: AlertCircle },
      Cancelled: { variant: "outline", icon: XCircle },
      Ended: { variant: "outline", icon: XCircle },
    };

    const config = statusConfig[subscription.status] || {
      variant: "outline" as const,
      icon: AlertCircle,
    };
    const Icon = config.icon;

    return (
      <TooltipWrap
        title={subscription.status}
        description={
          STATUS_DESCRIPTIONS[subscription.status] ??
          "Current Status for this Subscription"
        }
      >
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="w-3 h-3" />
        {subscription.status}
      </Badge>
      </TooltipWrap>
    );
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (onCancel) {
      try {
        setIsLoading(true);
        await onCancel();
      } finally {
        setIsLoading(false);
        setShowCancelDialog(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      await cancelSubscription(subscription.subscriptionId, false);
      window.location.reload();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    } finally {
      setIsLoading(false);
      setShowCancelDialog(false);
    }
  };

  return (
    <>
      <Card className="rounded-2xl border border-border shadow-lg bg-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700">
            <CreditCard className="h-5 w-5" />
          </div>
          <CardTitle className="text-lg font-semibold text-foreground">
            Current Subscription
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Manage your subscription and billing
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Plan
                </p>
                <p className="text-base font-semibold text-foreground mt-1.5">{subscription.planName}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </p>
                <div className="mt-1.5">{getStatusBadge()}</div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Billing Interval
                </p>
                <p className="mt-1.5 text-sm text-muted-foreground capitalize">{subscription.billingInterval}</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Amount
                </p>
                <p className="text-base font-semibold text-foreground mt-1.5">
                  {formatCurrency(subscription.totalAmount, subscription.currency)}
                  <span className="text-sm text-muted-foreground font-normal ml-2">
                    (incl. {formatCurrency(subscription.vatAmount, subscription.currency)} VAT)
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Next Billing Date
                </p>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {subscription.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}
                  <span className="text-sm text-muted-foreground ml-2">
                    ({subscription.daysUntilRenewal} days)
                  </span>
                </p>
              </div>
              {subscription.isInTrial && subscription.trialEndDate && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Trial Ends
                  </p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{formatDate(subscription.trialEndDate)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Cancel At Period End Alert */}
          {subscription.cancelAtPeriodEnd && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-900 text-sm font-semibold">
                Subscription Cancelled
              </AlertTitle>
              <AlertDescription className="text-yellow-800 text-sm">
                Your subscription will be cancelled at the end of this billing period ({subscription.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}). You'll retain access until then.
              </AlertDescription>
            </Alert>
          )}

          {/* Grace Period Alert */}
          {subscription.isInGracePeriod && subscription.gracePeriodEnd && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-900 text-sm font-semibold">
                Payment Issue
              </AlertTitle>
              <AlertDescription className="text-yellow-800 text-sm">
                We couldn't process your last payment. Your account will be suspended on{" "}
                {formatDate(subscription.gracePeriodEnd)} if payment is not received.
              </AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 border border-yellow-300 bg-yellow-100 text-yellow-900 hover:bg-yellow-200"
              >
                Update Payment Method
              </Button>
            </Alert>
          )}

          {/* Actions */}
          {!subscription.cancelAtPeriodEnd && (
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex items-center gap-1"
                onClick={handleCancelClick}
                disabled={isLoading}
              >
                Cancel Subscription
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-left pt-3">
              Are you sure you want to cancel your <strong>{subscription.planName}</strong> subscription?
              <br />
              <br />
              Your subscription will remain active until{" "}
              <strong>{subscription.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}</strong>,
              and you won't be charged again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Keep Subscription
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleConfirmCancel}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Yes, Cancel"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}